import React, { Dispatch, FC, SetStateAction } from 'react';
import styled from 'styled-components';
import { gql } from '@apollo/client';
import {
    Scalars,
    UspItemPageFieldsFragment,
    UspItemPageRecursiveFragment,
    UspItemsFragment,
} from '../generated/graphql';
import LinkField from './LinkField';
import { UspItemTitle } from './Header';

export const fragment = gql`
    fragment UspItems on usp_Entry {
        id
        title

        uspLink {
            ...LinkField
            element {
                ...UspItemPageFields
                ...UspItemPageRecursive
            }
        }
    }

    fragment UspItemPageRecursive on page_Entry {
        # Although this looks ugly as hell, it's actually what GQL recommends you to do! :(
        # https://github.com/graphql/graphql-spec/issues/91#issuecomment-254895093
        children {
            ...UspItemPageFields
            children {
                ...UspItemPageFields
                children {
                    ...UspItemPageFields
                    children {
                        ...UspItemPageFields
                    }
                }
            }
        }
    }

    fragment UspItemPageFields on page_Entry {
        slug
        id
        title
        uri
        settingsMenuHidden
        settingsMenuTitle
        parent {
            uri
            id
        }
    }
`;

interface Props {
    usp?: UspItemsFragment[];
    setSelectedPageIds: Dispatch<SetStateAction<EntryIds>>;
}

// convenience types
export type EntryIds = Array<Scalars['ID']>;
export type UspItemsPageFragment = UspItemPageRecursiveFragment & UspItemPageFieldsFragment;

const TopNavigation: FC<React.PropsWithChildren<Props>> = ({ usp, setSelectedPageIds }: Props) => (
    <Outer>
        <Inner>
            <NavigationList>
                {usp?.map((item, idx) => {
                    const uspLink = item.uspLink?.[0];
                    const content = (
                        <UspItemTitle first={idx === 0} highlight={false}>
                            {item?.title}
                        </UspItemTitle>
                    );

                    return (
                        uspLink && (
                            <UspItem key={item?.id}>
                                {!uspLink.element?.uri && !uspLink.url ? (
                                    <div>{content}</div>
                                ) : (
                                    <LinkField
                                        link={uspLink}
                                        onClick={() => {
                                            setSelectedPageIds([]);
                                        }}
                                    >
                                        {content}
                                    </LinkField>
                                )}
                            </UspItem>
                        )
                    );
                })}
            </NavigationList>
        </Inner>
    </Outer>
);

export default TopNavigation;

const Outer = styled.div`
    position: absolute;
    width: 100%;
    right: 0;
    left: 0;
    top: 7.2rem;
    height: calc(100vh - 7.2rem);
    overflow-x: hidden;
    overflow-y: auto;
    pointer-events: none;

    @media screen and (max-width: ${({ theme }) => theme.mediaQueries.s}) {
        &.enter > nav {
            transform: translateX(100vw);
        }
        &.enter-active > nav {
            transform: translateX(0);
        }
        &.exit > nav {
            transform: translateX(0);
        }
        &.exit-active > nav {
            transform: translateX(100vw);
        }
        &.exit-done > nav {
            display: none;
        }
        &.enter-done > nav {
            display: block;
        }
    }
    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.s}) {
        position: relative;
        overflow-x: auto;
        height: auto;
        top: 0;
        pointer-events: unset;
    }
`;
const Inner = styled.nav`
    transform: translateX(0);
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    transition: transform 0.5s ease-in-out;
    background: #214786;
    padding: 2.2rem;

    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.s}) {
        opacity: 1;
        display: flex;
        position: relative;
        align-items: center;
        padding: 0;
        transform: none;
        transition: none;
        height: auto;
        top: 0;
        box-shadow: none;
    }
`;

const NavigationList = styled.ul`
    margin: 0;
    padding: 0;

    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.s}) and (max-width: ${({ theme }) =>
            theme.mediaQueries.m}) {
        display: none;
    }

    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.m}) {
        display: flex;
        width: 100%;
    }
`;

export const UspItem = styled.li<{ isActive?: boolean }>`
    line-height: 1;
    margin: 0;
    position: relative;
    white-space: nowrap;
    display: flex;
    align-items: center;

    > a,
    > div {
        padding: 1.2rem 0;
        margin: 0 1.6rem 0 0;
        line-height: 1.6rem;
        text-decoration: none !important;
        display: flex;
        align-items: center;
        color: ${({ theme }) => theme.colors.primary[80]} !important;
        flex: 1;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        width: calc(100% - 8rem);
        display: block;
    }

    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.m}) {
        font-size: 1.4rem;
        font-weight: 800;
        font-family: poppins-bold, sans-serif;
        &:first-child {
            margin-left: -1.6rem;
            margin-right: auto;
        }
        > a,
        > div {
            padding: 1.5rem 1.6rem;
            margin: 0;
        }
    }
`;
